import { localeGroups } from '../content/localization.js';

/**
 * SEO Utility Module
 * Provides functions for managing dynamic meta tags and SEO data
 */

// Default SEO configuration
export const DEFAULT_SEO = {
      siteName: 'Ilmversity',
      siteUrl: 'https://ilmversity.com',
      defaultTitle: 'Ilmversity - Best Learning Management System in Pakistan',
      defaultDescription: 'Ilmversity is Pakistan\'s leading Learning Management System (LMS) designed for schools, colleges, and educational institutions. Streamline admissions, manage fees, track attendance, and enhance learning.',
      defaultImage: 'https://ilmversity.com/og-image.jpg',
      twitterHandle: '@ilmversity',
};

// Page-specific SEO data
export const PAGE_SEO = {
      home: {
            title: 'Ilmversity | School Management and Learning Software',
            description: 'Ilmversity is Pakistan\'s leading Learning Management System (LMS) designed for schools, colleges, and educational institutions in Lahore, Karachi, and across Pakistan. Streamline admissions, manage fees, track attendance, and enhance learning with our comprehensive education management platform.',
            keywords: 'learning management system, LMS Pakistan, school management system, education software, Ilmversity, student management, fee management, attendance tracking, online learning platform, educational technology',
            path: '/',
      },
      features: {
            title: 'Features - Comprehensive School Management Tools | Ilmversity',
            description: 'Discover Ilmversity\'s powerful features including student management, attendance tracking, fee management, exam management, timetable generation, and more. Everything you need to run your educational institution efficiently.',
            keywords: 'school management features, student information system, attendance management, fee collection, exam management, timetable software, educational tools',
            path: '/features',
      },
      lms: {
            title: 'LMS for Schools in Pakistan | Ilmversity',
            description: 'Connect assignments, learning resources, assessments and student progress with Ilmversity LMS for schools in Pakistan. Request a school-focused demo.',
            keywords: 'LMS Pakistan, learning management system Lahore, LMS Karachi, online learning platform, course management system, e-learning software Pakistan, digital classroom',
            path: '/best-learning-management-system-lahore-karachi-pakistan',
      },
      security: {
            title: 'Security & Data Protection | Ilmversity',
            description: 'Learn about Ilmversity\'s robust security measures and data protection protocols. We ensure your institution\'s data is safe with enterprise-grade encryption, regular backups, and compliance with international security standards.',
            keywords: 'data security, educational data protection, secure LMS, encrypted student data, GDPR compliance, data privacy',
            path: '/security',
      },
      downloadApp: {
            title: 'Download Ilmversity Mobile App | iOS & Android',
            description: 'Download the Ilmversity mobile app for iOS and Android. Access your school management system on the go with our feature-rich mobile application for teachers, students, and parents.',
            keywords: 'Ilmversity app, school management app, education app Pakistan, teacher app, student app, parent app',
            path: '/download-app',
      },
      about: {
            title: 'About Ilmversity - Transforming Education in Pakistan',
            description: 'Learn about Ilmversity\'s mission to transform education in Pakistan through innovative technology. Discover our story, values, and commitment to empowering educational institutions.',
            keywords: 'about Ilmversity, education technology Pakistan, EdTech company, school software provider',
            path: '/about-us',
      },
      whyIlmversity: {
            title: 'Why Choose Ilmversity? | Benefits & Advantages',
            description: 'Discover why schools across Pakistan choose Ilmversity. Learn about our unique advantages, comprehensive features, exceptional support, and proven track record in educational technology.',
            keywords: 'why Ilmversity, LMS benefits, school management advantages, education software comparison',
            path: '/why-ilmversity',
      },
      caseStudies: {
            title: 'Success Stories - Schools Transformed by Ilmversity',
            description: 'Read success stories from schools and educational institutions across Pakistan that have transformed their operations with Ilmversity. See real results and testimonials from our satisfied clients.',
            keywords: 'case studies, success stories, client testimonials, school transformation, Ilmversity reviews',
            path: '/case-studies',
      },
      contact: {
            title: 'Contact Us - Get in Touch with Ilmversity',
            description: 'Contact Ilmversity for inquiries, support, or to schedule a demo. Our team is ready to help you transform your educational institution with our comprehensive management system.',
            keywords: 'contact Ilmversity, customer support, sales inquiry, schedule demo',
            path: '/contact',
      },
      requestDemo: {
            title: 'Request Free Trial - Try Ilmversity LMS Today',
            description: 'Request a free trial of Ilmversity\'s Learning Management System. Experience firsthand how our platform can transform your school\'s operations. No credit card required.',
            keywords: 'free trial, LMS demo, request demo, try Ilmversity, school management trial',
            path: '/request-free-trial',
      },
      faq: {
            title: 'Frequently Asked Questions | Ilmversity',
            description: 'Find answers to common questions about Ilmversity\'s Learning Management System, features, pricing, implementation, and support. Get the information you need to make an informed decision.',
            keywords: 'FAQ, frequently asked questions, Ilmversity help, LMS questions',
            path: '/faq',
      },
      resources: {
            title: 'Resources & Insights - Educational Technology Blog | Ilmversity',
            description: 'Explore articles, guides, and insights on educational technology, school management best practices, and digital transformation in education. Stay updated with the latest trends.',
            keywords: 'education blog, EdTech articles, school management tips, educational resources',
            path: '/resources',
      },
      privacy: {
            title: 'Privacy Policy | Ilmversity',
            description: 'Read Ilmversity\'s privacy policy to understand how we collect, use, and protect your data. We are committed to maintaining the privacy and security of your information.',
            keywords: 'privacy policy, data protection, privacy terms',
            path: '/privacy',
      },
      terms: {
            title: 'Terms of Service | Ilmversity',
            description: 'Review Ilmversity\'s terms of service. These terms govern your use of our Learning Management System and related services.',
            keywords: 'terms of service, terms and conditions, user agreement',
            path: '/terms-of-service',
      },
      cookiePolicy: {
            title: 'Cookie Policy | Ilmversity',
            description: 'Learn about how Ilmversity uses cookies to improve your experience on our website. Understand what cookies we use and how to manage your preferences.',
            keywords: 'cookie policy, cookies, website tracking',
            path: '/cookie-policy',
      },
      enableThirdPartyCookies: {
            title: 'Enabling Third-Party Cookies for Mobile Safari | Ilmversity',
            description: 'Step-by-step guide to enable third-party cookies in Safari on iPhone and iPad. Turn off Prevent Cross-Site Tracking so you can sign in and use the Ilmversity portal.',
            keywords: 'enable third-party cookies Safari, mobile Safari cookies, prevent cross-site tracking, allow cookies iPhone, Safari cookies iPad, Ilmversity login help',
            path: '/enabling-third-party-cookies-for-mobile-safari',
      },
      caseStudyPSI: {
            title: 'Preparatory School Islamabad (PSI) Success Story | Ilmversity',
            description: 'Discover how Preparatory School Islamabad modernized its fee collection and school management using Ilmversity\'s LMS platform, improving timely payments and administrative efficiency.',
            keywords: 'PSI case study, Preparatory School Islamabad, school management Pakistan, fee management success story',
            path: '/case-studies/psi',
      },
      caseStudyNordic: {
            title: 'Nordic International School Success Story | Ilmversity',
            description: 'See how Nordic International School implemented Ilmversity\'s learning management system to streamline academic and administrative operations for its international student body.',
            keywords: 'Nordic International School, LMS case study, international school management, Ilmversity success story',
            path: '/case-studies/nordic',
      },
      caseStudyWhales: {
            title: 'Whales College Digital Transformation | Ilmversity',
            description: 'Learn how Whales College leveraged Ilmversity to digitize its college management processes, improving student tracking, fee management, and communication.',
            keywords: 'Whales College, college management system, digital transformation, Ilmversity case study',
            path: '/case-studies/whales',
      },
      caseStudyEureka: {
            title: 'Eureka-Pingu\'s English School Finance Management | Ilmversity',
            description: 'Read how Eureka-Pingu\'s English School found a cost-effective solution with Ilmversity to manage school finances, student records, and daily operations collectively.',
            keywords: "Eureka Pingu school, school finance management, cost-effective LMS, Ilmversity case study",
            path: '/case-studies/eureka',
      },
      caseStudyDAM: {
            title: 'Dar e Arqam Schools Case Study | Ilmversity',
            description: 'Explore how Dar e Arqam Maymar Campus adopted Ilmversity to manage all school-related activities — from admissions and attendance to fee collection and academic records.',
            keywords: 'Dar e Arqam, school management software, Pakistan school LMS, Ilmversity case study',
            path: '/case-studies/dam',
      },
      caseStudyZICAS: {
            title: 'ZICAS School Digitization Success Story | Ilmversity',
            description: 'Find out how ZICAS used Ilmversity\'s platform to digitize its school operations, improving efficiency, transparency, and communication across all departments.',
            keywords: 'ZICAS, school digitization, education management system, Ilmversity case study Pakistan',
            path: '/case-studies/zicas',
      },
};

/**
 * Update the document title
 */
export const updateTitle = (title) => {
      document.title = title || DEFAULT_SEO.defaultTitle;
};

/**
 * Update or create a meta tag
 */
export const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!element) {
            element = document.createElement('meta');
            element.setAttribute(attribute, name);
            document.head.appendChild(element);
      }

      element.setAttribute('content', content);
};

/**
 * Update canonical URL
 */
export const updateCanonical = (url) => {
      let canonical = document.querySelector('link[rel="canonical"]');

      if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
      }

      canonical.setAttribute('href', url);
};

/**
 * Update all SEO tags for a page
 */
const updateLegacySEO = ({
      title,
      description,
      keywords,
      image,
      url,
      type = 'website',
}) => {
      const fullTitle = title || DEFAULT_SEO.defaultTitle;
      const fullDescription = description || DEFAULT_SEO.defaultDescription;
      const fullImage = image || DEFAULT_SEO.defaultImage;
      const fullUrl = url || DEFAULT_SEO.siteUrl;

      // Update title
      updateTitle(fullTitle);

      // Update meta tags
      updateMetaTag('description', fullDescription);
      if (keywords) {
            updateMetaTag('keywords', keywords);
      }

      // Update Open Graph tags
      updateMetaTag('og:title', fullTitle, true);
      updateMetaTag('og:description', fullDescription, true);
      updateMetaTag('og:image', fullImage, true);
      updateMetaTag('og:url', fullUrl, true);
      updateMetaTag('og:type', type, true);
      updateMetaTag('og:site_name', DEFAULT_SEO.siteName, true);

      // Update Twitter Card tags
      updateMetaTag('twitter:title', fullTitle, true);
      updateMetaTag('twitter:description', fullDescription, true);
      updateMetaTag('twitter:image', fullImage, true);
      updateMetaTag('twitter:url', fullUrl, true);
      updateMetaTag('twitter:card', 'summary_large_image', true);

      // Update canonical URL
      updateCanonical(fullUrl);
};

/**
 * Add or replace a JSON-LD structured data block.
 * Each block is keyed by `id` so a page can own more than one and clean up on unmount.
 */
export const updateJsonLd = (id, data) => {
      const scriptId = `jsonld-${id}`;
      let script = document.getElementById(scriptId);

      if (!script) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.id = scriptId;
            document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(data);
};

/**
 * Remove a JSON-LD structured data block added by updateJsonLd
 */
export const removeJsonLd = (id) => {
      const script = document.getElementById(`jsonld-${id}`);
      if (script) {
            script.remove();
      }
};

/**
 * Get SEO data for a specific page
 */
export const getPageSEO = (pageKey) => {
      return PAGE_SEO[pageKey] || {
            title: DEFAULT_SEO.defaultTitle,
            description: DEFAULT_SEO.defaultDescription,
            path: '/',
      };
};

/**
 * Generate full URL from path
 */
export const getFullUrl = (path) => {
      return `${DEFAULT_SEO.siteUrl}${normalizePath(path)}`;
};

export const normalizePath = (path = '/') => {
      const clean = path.split(/[?#]/)[0].replace(/\/+$/, '');
      return clean || '/';
};

export function buildMetadata(data, pathname) {
      const path = normalizePath(pathname);
      const url = getFullUrl(data.canonicalPath || path);
      const noindex = data.noindex || import.meta.env?.MODE === 'staging';
      const alternates = localeGroups.find(group => group.some(item => item.path === path)) || [];
      const organization = {
            '@context': 'https://schema.org', '@type': 'Organization',
            '@id': `${DEFAULT_SEO.siteUrl}/#organization`, name: 'Ilmversity',
            url: `${DEFAULT_SEO.siteUrl}/`, logo: `${DEFAULT_SEO.siteUrl}/logo.png`,
      };
      const breadcrumbs = path === '/' ? [] : [{
            '@context': 'https://schema.org', '@type': 'BreadcrumbList',
            itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Home', item: getFullUrl('/') },
                  ...(data.breadcrumbs || []).map((item, i) => ({ '@type': 'ListItem', position: i + 2, name: item.name, item: getFullUrl(item.path) })),
                  { '@type': 'ListItem', position: (data.breadcrumbs?.length || 0) + 2, name: data.breadcrumbTitle || data.title.split('|')[0].trim(), item: url },
            ],
      }];
      return {
            ...data, url, path, type: data.type || 'website', alternates,
            language: alternates.find(item => item.path === path)?.language || 'en',
            image: new URL(data.image || DEFAULT_SEO.defaultImage, DEFAULT_SEO.siteUrl).href,
            robots: noindex ? 'noindex, follow' : 'index, follow',
            structuredData: [organization, ...breadcrumbs, ...(data.structuredData || [])],
      };
}

export function updateSEO(metadata) {
      updateLegacySEO(metadata);
      updateMetaTag('robots', metadata.robots);
      document.documentElement.lang = metadata.language;
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(node => node.remove());
      for (const item of metadata.alternates) {
            const link = document.createElement('link');
            link.rel = 'alternate'; link.hreflang = item.language; link.href = getFullUrl(item.path);
            document.head.appendChild(link);
      }
      document.querySelector('meta[name="title"]')?.remove();
      document.querySelector('meta[name="keywords"]')?.remove();
      // Every page replaces its schema, including on client-side navigation.
      document.querySelectorAll('script[data-page-schema]').forEach(node => node.remove());
      for (const data of metadata.structuredData) {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.dataset.pageSchema = '';
            script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
            document.head.appendChild(script);
      }
}

export const escapeHtml = value => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

export function renderMetadata(data) {
      const meta = (name, value, attr = 'name') => `<meta ${attr}="${name}" content="${escapeHtml(value)}" />`;
      return [
            `<title>${escapeHtml(data.title)}</title>`,
            meta('description', data.description), meta('robots', data.robots),
            `<link rel="canonical" href="${escapeHtml(data.url)}" />`,
            ...data.alternates.map(item => `<link rel="alternate" hreflang="${item.language}" href="${getFullUrl(item.path)}" />`),
            ...Object.entries({ title: data.title, description: data.description, url: data.url, image: data.image, type: data.type, site_name: 'Ilmversity' }).map(([name, value]) => meta(`og:${name}`, value, 'property')),
            ...Object.entries({ card: 'summary_large_image', title: data.title, description: data.description, image: data.image, url: data.url }).map(([name, value]) => meta(`twitter:${name}`, value, 'property')),
            ...data.structuredData.map(item => `<script type="application/ld+json" data-page-schema>${JSON.stringify(item).replace(/</g, '\\u003c')}</script>`),
      ].join('\n');
}
